import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {AbortController | undefined} */
  #underlayCloseController;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #underlayCloseTimer;

  #cancelUnderlayClose() {
    this.#underlayCloseController?.abort();
    clearTimeout(this.#underlayCloseTimer);
    this.#underlayCloseController = undefined;
    this.#underlayCloseTimer = undefined;
  }

  #finishUnderlayClose() {
    this.#cancelUnderlayClose();
    const header = this.headerComponent;
    const underlay = header?.querySelector('.header__underlay-open');
    if (!(underlay instanceof HTMLElement) || !header) return;
    const controller = new AbortController();
    this.#underlayCloseController = controller;
    const finish = () => {
      if (controller.signal.aborted || this.#state.activeItem) return;
      this.#cancelUnderlayClose();
      delete header.dataset.menuUnderlay;
    };
    underlay.addEventListener('transitionend', (event) => {
      if (event.target === underlay && event.propertyName === 'height') finish();
    }, { signal: controller.signal });
    const style = getComputedStyle(underlay);
    const seconds = (/** @type {string} */ value) => value.trim().endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
    const durations = style.transitionDuration.split(',').map(seconds);
    const delays = style.transitionDelay.split(',').map(seconds);
    const duration = Math.max(...durations.map((value, index) => value + delays[index % delays.length]));
    this.#underlayCloseTimer = setTimeout(finish, duration + 50);
  }

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('keydown', this.#dropdownKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelUnderlayClose();
    if (this.headerComponent) delete this.headerComponent.dataset.menuUnderlay;
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    clearTimeout(this.#deactivationTimer);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.removeEventListener('keydown', this.#dropdownKeydown);
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#state.activeItem && !this.contains(this.#state.activeItem)) {
      this.#deactivate(this.#state.activeItem, true);
      this.#cleanupMutationObserver();
    }
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.querySelectorAll('.header-dropdown__item[data-open]').forEach((item) => this.#positionFlyout(item));
  }, 100);

  /** @param {PointerEvent | FocusEvent | KeyboardEvent} event */
  openDropdown(event) {
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('.header-dropdown__item');
    if (!(item instanceof HTMLElement)) return;
    const link = item.querySelector(':scope > a[aria-haspopup]');
    if (!link) return;
    item.dataset.open = '';
    link.setAttribute('aria-expanded', 'true');
    this.#positionFlyout(item);
  }

  /** @param {PointerEvent | FocusEvent} event */
  closeDropdown(event) {
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('.header-dropdown__item');
    if (!(item instanceof HTMLElement)) return;
    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;
    if (event.type === 'pointerleave' && item.contains(document.activeElement)) return;
    this.#closeFlyout(item);
    const submenu = findSubmenu(this.#state.activeItem);
    if (!(event.relatedTarget instanceof Node) || !submenu?.contains(event.relatedTarget)) this.deactivate(event);
  }

  /** @param {Element} item */
  #positionFlyout(item) {
    if (!(item instanceof HTMLElement)) return;
    const panel = item.querySelector(':scope > .header-dropdown__flyout');
    if (!(panel instanceof HTMLElement)) return;
    delete item.dataset.openLeft;
    item.style.removeProperty('--dropdown-flyout-top');
    panel.style.removeProperty('max-width');
    const rtl = getComputedStyle(item).direction === 'rtl';
    const itemRect = item.getBoundingClientRect();
    const inlineEndSpace = rtl ? itemRect.left - 8 : window.innerWidth - itemRect.right - 8;
    const inlineStartSpace = rtl ? window.innerWidth - itemRect.right - 8 : itemRect.left - 8;
    const width = panel.getBoundingClientRect().width;
    const flip = inlineEndSpace < width && inlineStartSpace > inlineEndSpace;
    if (flip) item.dataset.openLeft = '';
    panel.style.maxWidth = `${Math.max(1, flip ? inlineStartSpace : inlineEndSpace)}px`;
    const rect = panel.getBoundingClientRect();
    const offset = Math.min(0, window.innerHeight - 8 - rect.bottom);
    item.style.setProperty('--dropdown-flyout-top', `${offset}px`);
  }

  /** @param {HTMLElement} item */
  #closeFlyout(item) {
    delete item.dataset.open;
    item.querySelector(':scope > a[aria-haspopup]')?.setAttribute('aria-expanded', 'false');
  }

  /** @param {KeyboardEvent} event */
  #dropdownKeydown = (event) => {
    if (!(event.target instanceof Element)) return;
    const submenu = findSubmenu(this.#state.activeItem);
    if (!submenu?.classList.contains('menu-list__submenu--dropdown')) return;
    const item = event.target.closest('.header-dropdown__item');
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight') && item instanceof HTMLElement) {
      this.openDropdown(event);
      const firstLink = item.querySelector('.header-dropdown__flyout a');
      if (firstLink instanceof HTMLElement) {
        event.preventDefault();
        firstLink.focus();
      }
    }
    if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft') && item instanceof HTMLElement && event.target.closest('.header-dropdown__flyout')) {
      event.preventDefault();
      const link = item.querySelector(':scope > a');
      if (link instanceof HTMLElement) link.focus();
      this.#closeFlyout(item);
      return;
    }
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    if (item instanceof HTMLElement && item.hasAttribute('data-open')) {
      const link = item.querySelector(':scope > a');
      if (link instanceof HTMLElement) link.focus();
      this.#closeFlyout(item);
      return;
    }
    this.#state.activeItem?.focus();
    this.#deactivate(this.#state.activeItem, true);
  };

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivationTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    clearTimeout(this.#deactivationTimer);
    this.#deactivationTimer = undefined;

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
        previousSubmenu.querySelectorAll('.header-dropdown__item').forEach((item) => {
          if (item instanceof HTMLElement) this.#closeFlyout(item);
        });
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    this.#cancelUnderlayClose();
    this.#cleanupMutationObserver();
    const isDropdown = isDefaultSlot && submenu?.classList.contains('menu-list__submenu--dropdown');
    this.headerComponent.dataset.menuUnderlay = isDropdown ? 'dropdown' :
      isDefaultSlot && submenu?.classList.contains('menu-list__submenu--product-split') ?
        (submenu.dataset.productSplitWidth === 'full' ? 'product-full' : 'product-contained') : 'overflow';

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      delete submenu.dataset.dismissed;
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      if (!isDropdown) this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (this.#state.activeItem === item && submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver?.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      const observer = this.#submenuMutationObserver;
      setTimeout(() => {
        if (this.#submenuMutationObserver === observer) this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = isDropdown ? 0 : submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    if (event.type === 'pointerleave') {
      const activeItem = this.#state.activeItem;
      clearTimeout(this.#deactivationTimer);
      this.#deactivationTimer = setTimeout(() => {
        this.#deactivationTimer = undefined;

        if (activeItem?.matches(':hover') || menu?.matches(':hover')) return;
        if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

        this.#deactivate(activeItem);
      }, 120);
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    clearTimeout(this.#deactivationTimer);
    this.#deactivationTimer = undefined;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.#cleanupMutationObserver();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (this.headerComponent?.dataset.menuUnderlay !== 'dropdown') this.#finishUnderlayClose();

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      if (force && submenu.classList.contains('menu-list__submenu--dropdown')) submenu.dataset.dismissed = '';
      submenu.querySelectorAll('.header-dropdown__item').forEach((item) => {
        if (item instanceof HTMLElement) this.#closeFlyout(item);
      });
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
